import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import FormField from '../../../../components/common/FormField';
import { InstallationData } from '../../../../models/CompletionModels';
import { useAppSelector, useAppDispatch } from '../../../../hooks/storeHooks';
import { setSelectedLiftType } from '../CreateCompletionsSlice';
import { useFormValidation, useFormFieldHandler, validateTabFields } from '../../../../utilities/completionsUtils';

interface CompletionsInstallationTabProps {
  installation: InstallationData;
  setInstallation: React.Dispatch<React.SetStateAction<InstallationData>>;
  externalValidationErrors?: { [fieldName: string]: string };
  isEditMode?: boolean;
}

const CompletionsInstallationTab = forwardRef<any, CompletionsInstallationTabProps>(
  ({ installation, setInstallation, externalValidationErrors = {}, isEditMode = false }, ref) => {
    const dispatch = useAppDispatch();

    const validation = useFormValidation({ tabName: 'installation' });
    const handleFieldChange = useFormFieldHandler(setInstallation, validation);
    const completions = useAppSelector((state: any) => state.WellCompletions.currentCompletion.data || []);

    const displayErrors = validateTabFields('installation', installation, { completions, isEditMode });
    const mergedDisplayErrors = {
      ...displayErrors,
      ...externalValidationErrors,
    };

    const handleLiftTypeChange = (value: string) => {
      handleFieldChange('liftType', value);
      dispatch(setSelectedLiftType(value));
    };

    const handleManufacturerChange = (value: string) => {
      handleFieldChange('manufacturer', value);
      // Reset manufacturer controller when manufacturer changes
      handleFieldChange('manufacturerController', '');
    };

    useImperativeHandle(ref, () => ({
      triggerValidation: (data: InstallationData) => {
        validation.validateAllFields(data);
      },
    }));

    const liftTypes = useAppSelector((state: any) => state.CreateCompletions.liftTypes.data);
    const manufacturersWithControllers = useAppSelector(
      (state: any) => state.CreateCompletions.manufacturersWithControllers.data,
    );
    const runStatusData = useAppSelector((state: any) => state.rodliftwellstatus.data?.imageOverlayItems);

    const runStatusObj = Array.isArray(runStatusData)
      ? runStatusData.find((item: any) => item.overlayField === 6)
      : undefined;
    const runStatusValue = runStatusObj?.value ?? '';

    // Auto-populate status field with well run status
    useEffect(() => {
      if (runStatusValue && !installation.status) {
        setInstallation((prev) => ({
          ...prev,
          status: runStatusValue,
        }));
      }
    }, [runStatusValue, installation.status, setInstallation]);

    // Transform API data to FormField options format
    const liftTypeOptions = Array.isArray(liftTypes)
      ? liftTypes.map((lt: any) => ({
          key: lt.Key,
          value: lt.Value,
        }))
      : [];

    const manufacturerOptions = Array.isArray(manufacturersWithControllers)
      ? manufacturersWithControllers.map((m: any) => ({
          key: m.Name,
          value: m.Name,
        }))
      : [];

    const selectedManufacturer = Array.isArray(manufacturersWithControllers)
      ? manufacturersWithControllers.find((m: any) => m.Name === installation.manufacturer)
      : undefined;

    const manufacturerControllerOptions = selectedManufacturer?.Controllers
      ? selectedManufacturer.Controllers.map((mc: any) => ({
          key: mc.Id,
          value: mc.Name,
        }))
      : [];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Lift type'
            name='liftType'
            type='select'
            value={installation.liftType}
            onChange={(_, v) => {
              handleLiftTypeChange(v as string);
            }}
            options={liftTypeOptions}
            error={mergedDisplayErrors.liftType}
            required
          />
          <FormField
            label='Manufacturer'
            name='manufacturer'
            type='select'
            value={installation.manufacturer}
            onChange={(_, v) => handleManufacturerChange(v as string)}
            options={manufacturerOptions}
          />
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Manufacturer controller'
            name='manufacturerController'
            type='select'
            value={installation.manufacturerController}
            onChange={(_, v) => handleFieldChange('manufacturerController', v)}
            options={manufacturerControllerOptions}
          />
          <FormField
            label='Install number'
            name='installNumber'
            type='text'
            value={installation.installNumber}
            onChange={(_, v) => handleFieldChange('installNumber', v)}
            error={mergedDisplayErrors.installNumber}
            required
          />
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Status'
            name='status'
            type='text'
            value={isEditMode ? installation.status : runStatusValue}
            disabled
            onChange={() => undefined}
          />
          <div style={{ flex: 1 }} />
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div
              className={`checkbox ${installation.blockUpdates ? 'enabled' : ''}`}
              onClick={() => handleFieldChange('blockUpdates', !installation.blockUpdates)}
            >
              {installation.blockUpdates && <div className='checkbox-icon' />}
            </div>
            <span
              style={{ marginLeft: 8, cursor: 'pointer' }}
              onClick={() => handleFieldChange('blockUpdates', !installation.blockUpdates)}
            >
              Block updates
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div
              className={`checkbox ${installation.excludeFromReports ? 'enabled' : ''}`}
              onClick={() => handleFieldChange('excludeFromReports', !installation.excludeFromReports)}
            >
              {installation.excludeFromReports && <div className='checkbox-icon' />}
            </div>
            <span
              className='enabled-text'
              style={{ marginLeft: 8, cursor: 'pointer' }}
              onClick={() => handleFieldChange('excludeFromReports', !installation.excludeFromReports)}
            >
              Exclude from reports
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div
              className={`checkbox ${installation.purchasedSystem ? 'enabled' : ''}`}
              onClick={() => handleFieldChange('purchasedSystem', !installation.purchasedSystem)}
            >
              {installation.purchasedSystem && <div className='checkbox-icon' />}
            </div>
            <span
              className='enabled-text'
              style={{ marginLeft: 8, cursor: 'pointer' }}
              onClick={() => handleFieldChange('purchasedSystem', !installation.purchasedSystem)}
            >
              Purchased system
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div
              className={`checkbox ${installation.usedSystem ? 'enabled' : ''}`}
              onClick={() => handleFieldChange('usedSystem', !installation.usedSystem)}
            >
              {installation.usedSystem && <div className='checkbox-icon' />}
            </div>
            <span
              className='enabled-text'
              style={{ marginLeft: 8, cursor: 'pointer' }}
              onClick={() => handleFieldChange('usedSystem', !installation.usedSystem)}
            >
              Used system
            </span>
          </div>
        </div>
      </div>
    );
  },
);

export default CompletionsInstallationTab;
